// src/pages/ProductsServices.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase-config';
import { collection, getDocs } from 'firebase/firestore';
import '../styles/ProductsServices.css';

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const ProductsServices = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounced(searchQuery, 220);

  const [viewMode, setViewMode] = useState('grid'); // grid | list
  const [sortBy, setSortBy] = useState('relevance'); // relevance | title-asc | title-desc | newest
  const [filterCategory, setFilterCategory] = useState('');

  // Load items from Firestore
  useEffect(() => {
    let mounted = true;
    async function fetchItems() {
      setLoading(true);
      setError(null);
      try {
        const querySnapshot = await getDocs(collection(db, 'products'));
        const itemsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (!mounted) return;
        setItems(itemsList);
      } catch (err) {
        console.error('Error fetching items:', err);
        if (mounted) setError('Failed to load items. Please try again later.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    void fetchItems();
    return () => { mounted = false; };
  }, []);

  // Build category list from items (if available)
  const categories = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      if (Array.isArray(it.industries)) {
        it.industries.forEach(i => set.add(i));
      } else if (it.category) {
        set.add(it.category);
      }
    }
    return Array.from(set).sort();
  }, [items]);

  // Filter / search / sort
  const filtered = useMemo(() => {
    const q = (debouncedQuery || '').trim().toLowerCase();
    let out = items.slice();

    if (filterCategory) {
      out = out.filter(it => {
        if (Array.isArray(it.industries)) return it.industries.includes(filterCategory);
        return (it.category || '').toString().toLowerCase() === filterCategory.toLowerCase();
      });
    }

    if (q) {
      out = out.filter(it => {
        const title = (it.title || '').toString().toLowerCase();
        const desc = (it.description || '').toString().toLowerCase();
        return title.includes(q) || desc.includes(q);
      });
    }

    if (sortBy === 'title-asc') out.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    if (sortBy === 'title-desc') out.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
    if (sortBy === 'newest') out.sort((a, b) => new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0));

    return out;
  }, [items, debouncedQuery, filterCategory, sortBy]);

  // Lock body scroll when modal open
  useEffect(() => {
    if (selectedItem) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [selectedItem]);

  // Close modal on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedItem(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // navigate to Home contact with prefill
  const openContactForItem = useCallback((item) => {
    if (!item) {
      navigate('/', { state: { scrollToContact: true } });
      setSelectedItem(null);
      return;
    }

    const message = [
      `Hello Afework team,`,
      ``,
      `I'm interested in the price and availability of the product: "${item.title}".`,
      `Could you please provide a quotation including unit price, lead time, minimum order quantity (if any), and shipping options to our region?`,
      ``,
      `Preferred currency (if applicable), and any volume discounts would be appreciated.`,
      ``,
      `Thank you.`
    ].join('\n');

    // Navigate back to home and pass the contact prefill (topic + message)
    navigate('/', {
      state: {
        scrollToContact: true,
        contact: {
          concern: 'medical', // set the topic to Medical procurement
          message
        }
      }
    });

    setSelectedItem(null);
  }, [navigate]);

  return (
    <div className="ps-page">
      <header className="ps-header">
        <div className="ps-header-inner">
          <div>
            <h1 className="ps-title">Product Catalogue</h1>
            <p className="ps-sub">Explore our vetted medical & industrial equipment. Click an item to view details or request a quote.</p>
          </div>

          <div className="ps-controls">
            <div className="search-wrap">
              <input
                aria-label="Search products"
                className="ps-search"
                placeholder="Search products or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="ps-btn" onClick={() => setSearchQuery('')} aria-label="Clear search">✕</button>
            </div>

            <select className="ps-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">All categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <select className="ps-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="relevance">Sort: Relevance</option>
              <option value="newest">Sort: Newest</option>
              <option value="title-asc">Title A → Z</option>
              <option value="title-desc">Title Z → A</option>
            </select>

            <div className="view-toggle" role="tablist" aria-label="View mode">
              <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} aria-pressed={viewMode === 'grid'} onClick={() => setViewMode('grid')} title="Grid view">▦</button>
              <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')} title="List view">☰</button>
            </div>
          </div>
        </div>
      </header>

      <main className="ps-main">
        {loading ? (
          <div className="ps-empty">Loading items…</div>
        ) : error ? (
          <div className="ps-empty error">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="ps-empty">No items match your search / filter.</div>
        ) : (
          <div className={`ps-grid ${viewMode === 'list' ? 'list' : 'grid'}`}>
            {filtered.map(item => (
              <article
                key={item.id}
                className="ps-card"
                onClick={() => setSelectedItem(item)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => { if (e.key === 'Enter') setSelectedItem(item); }}
                aria-label={`View ${item.title}`}
              >
                <div className="ps-media">
                  {item.image ? <img src={item.image} alt={item.title} loading="lazy" /> : <div className="ps-placeholder">No image</div>}
                </div>
                <div className="ps-body">
                  <h3 className="ps-name">{item.title}</h3>
                  <p className="ps-desc">{item.description}</p>
                </div>
                <div className="ps-footer">
                  <small className="ps-meta">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}</small>
                  <button
                    className="ps-quick"
                    onClick={(e) => { e.stopPropagation(); openContactForItem(item); }}
                    aria-label={`Request quote for ${item.title}`}
                  >
                    Request quote
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* Modal */}
      {selectedItem && (
        <>
          <div className="ps-overlay" onClick={() => setSelectedItem(null)} />
          <aside className="ps-modal" role="dialog" aria-modal="true" aria-label={selectedItem.title}>
            <button className="ps-modal-close" onClick={() => setSelectedItem(null)} aria-label="Close">×</button>
            <div className="ps-modal-inner">
              <div className="ps-modal-media">
                {selectedItem.image ? <img src={selectedItem.image} alt={selectedItem.title} /> : <div className="ps-placeholder-large">No image</div>}
              </div>
              <div className="ps-modal-body">
                <h2>{selectedItem.title}</h2>
                <p className="ps-muted">{selectedItem.description}</p>

                {selectedItem.industries && Array.isArray(selectedItem.industries) && (
                  <div className="ps-tags">
                    {selectedItem.industries.map(t => <span key={t} className="ps-tag">{t}</span>)}
                  </div>
                )}

                <div className="ps-modal-actions">
                  <button className="btn btn-primary" onClick={() => openContactForItem(selectedItem)}>Request quote</button>
                  <button className="btn btn-outline" onClick={() => navigate('/', { state: { scrollToContact: true } })}>Contact us</button>
                </div>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default ProductsServices;
